import React, { useState, useEffect } from 'react';
import { getPackagesByType, PACKAGE_TYPES } from '../services/packageService';
import { getUserActiveSubscription, createSubscription, hasUsedFreePackage } from '../services/subscriptionService';
import { initiateRazorpayPayment, calculateGST, formatAmount } from '../services/razorpayService';
import { createPaymentRecord, markPaymentSuccess, markPaymentFailed } from '../services/paymentService';
import LoadingSpinner from './LoadingSpinner';

const JobSeekerPackagePurchase = ({ user, userData, onPurchaseSuccess, showCurrentPackage = true }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [hasUsedFree, setHasUsedFree] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      console.log('🔍 JobSeekerPackagePurchase: Fetching JOB_SEEKER packages');

      // Fetch job seeker packages
      const packagesResult = await getPackagesByType(PACKAGE_TYPES.JOB_SEEKER);
      if (packagesResult.success) {
        console.log(`✅ Found ${packagesResult.packages.length} job seeker packages`);
        setPackages(packagesResult.packages);
      } else {
        console.error('❌ Failed to fetch packages:', packagesResult.error);
      }

      // Fetch current subscription
      if (user?.uid) {
        const subscriptionResult = await getUserActiveSubscription(user.uid);
        if (subscriptionResult.success && subscriptionResult.subscription) {
          console.log('✅ Found active subscription:', subscriptionResult.subscription.packageName);
          setCurrentSubscription(subscriptionResult.subscription);
        } else {
          console.log('ℹ️ No active subscription found');
        }

        const freeUsageResult = await hasUsedFreePackage(user.uid);
        if (freeUsageResult.success) {
          setHasUsedFree(freeUsageResult.hasUsedFree);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (pkg) => {
    if (!user?.uid) {
      alert('Please login to purchase a package');
      return;
    }

    setPurchasing(pkg.id);

    try {
      console.log('🛒 [JobSeekerPurchase] Starting purchase for:', pkg.name);

      // Check for free package
      if (pkg.price === 0) {
        if (hasUsedFree) {
          alert('You have already used your free package trial.');
          setPurchasing(null);
          return;
        }

        console.log('🎁 Activating free package:', pkg.name);

        const subscriptionData = {
          userId: user.uid,
          packageId: pkg.id,
          packageName: pkg.name,
          packageType: pkg.type,
          price: 0,
          duration: pkg.duration,
          features: pkg.features || [],
          maxApplications: pkg.maxApplications || 0,
          resumeTemplates: pkg.resumeTemplates || 0,
          profileBoost: pkg.profileBoost || false,
          userType: 'jobseeker',
          paymentId: `FREE_${Date.now()}`,
          razorpayPaymentId: 'FREE_TIER',
          razorpayOrderId: 'FREE_TIER',
        };

        const subscriptionResult = await createSubscription(subscriptionData);

        if (subscriptionResult.success) {
          alert(`✅ Successfully activated ${pkg.name}!`);
          setHasUsedFree(true);
          fetchData(); // Refresh data
          if (onPurchaseSuccess) {
            onPurchaseSuccess(subscriptionResult.subscription);
          }
        } else {
          console.error('Failed to activate free package:', subscriptionResult.error);
          alert(`Failed to activate free package: ${subscriptionResult.error}`);
        }

        setPurchasing(null);
        return;
      }

      // Calculate amount with GST to be consistent with display
      const gstCalculation = calculateGST(pkg.price);
      const totalAmount = gstCalculation.totalAmount;
      const baseAmount = pkg.price;

      console.log('💰 [JobSeekerPurchase] Base Price:', baseAmount);
      console.log('💰 [JobSeekerPurchase] Total Price (w/ GST):', totalAmount);

      // Create payment record first
      const paymentData = {
        userId: user.uid,
        packageId: pkg.id,
        packageName: pkg.name,
        amount: totalAmount, // Total amount user pays
        currency: 'INR',
        gstAmount: gstCalculation.gstAmount,
        baseAmount: baseAmount,
        totalAmount: totalAmount,
        userEmail: userData?.email || user.email,
        userName: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : user.displayName,
        userPhone: userData?.phone || '',
      };

      const paymentResult = await createPaymentRecord(paymentData);

      if (!paymentResult.success) {
        throw new Error('Failed to create payment record');
      }

      const firebasePaymentId = paymentResult.paymentId;
      console.log('✅ [JobSeekerPurchase] Payment record created:', firebasePaymentId);

      // Initiate Razorpay payment with TOTAL amount
      console.log('🚀 [JobSeekerPurchase] Invoking initiateRazorpayPayment with:', {
        amount: totalAmount,
        currency: 'INR',
        packageName: pkg.name
      });

      const razorpayResult = await initiateRazorpayPayment({
        amount: totalAmount,
        currency: 'INR',
        packageName: pkg.name,
        packageId: pkg.id,
        userEmail: userData?.email || user.email,
        userName: userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}`.trim() : user.displayName,
        userPhone: userData?.phone || '',
        userId: user.uid,
        onSuccess: async (paymentResponse) => {
          console.log('✅ [JobSeekerPurchase] onSuccess callback triggered:', paymentResponse);

          // Update payment record
          await markPaymentSuccess(firebasePaymentId, paymentResponse);

          // Create subscription
          const subscriptionData = {
            userId: user.uid,
            packageId: pkg.id,
            packageName: pkg.name,
            packageType: pkg.type,
            price: pkg.price,
            duration: pkg.duration,
            features: pkg.features || [],
            maxApplications: pkg.maxApplications || 0,
            resumeTemplates: pkg.resumeTemplates || 0,
            profileBoost: pkg.profileBoost || false,
            userType: 'jobseeker',
            paymentId: firebasePaymentId,
            razorpayPaymentId: paymentResponse.paymentId,
            razorpayOrderId: paymentResponse.orderId || null,
          };

          const subscriptionResult = await createSubscription(subscriptionData);

          if (subscriptionResult.success) {
            alert(`✅ Successfully purchased ${pkg.name}! You can now apply for jobs.`);
            fetchData();
            if (onPurchaseSuccess) {
              onPurchaseSuccess(subscriptionResult.subscription);
            }
          } else {
            console.error('❌ [JobSeekerPurchase] Subscription creation failed:', subscriptionResult.error);
            alert('Payment successful but failed to activate subscription. Please contact support.');
          }

          setPurchasing(null);
        },
        onFailure: async (error) => {
          console.error('❌ [JobSeekerPurchase] onFailure callback triggered:', error);

          // Update payment record
          await markPaymentFailed(firebasePaymentId, error.error || 'Payment failed');

          if (!error.cancelled) {
            alert('Payment failed: ' + (error.error || 'Unknown error'));
          }

          setPurchasing(null);
        },
      });

      console.log('🏁 [JobSeekerPurchase] initiateRazorpayPayment returned:', razorpayResult);
      if (!razorpayResult.success) {
        console.error('❌ [JobSeekerPurchase] Init Failed (Synchronous):', razorpayResult.error);
        throw new Error(razorpayResult.error || 'Payment initiation returned failure');
      }

    } catch (error) {
      console.error('❌ [JobSeekerPurchase] Error:', error);

      // Show user-friendly error message
      if (error.message.includes('SERVER CONFIG ERROR') || error.message.includes('DEMO MODE')) {
        alert('Payment gateway is not configured correctly. Please contact support and try again later.');
      } else if (error.message.includes('Authentication failed')) {
        alert('Payment gateway authentication issue. Please contact support.');
      } else {
        alert('Error: ' + error.message);
      }

      setPurchasing(null);
    }
  };

  const getSubscriptionStatus = () => {
    if (!currentSubscription) return null;

    const endDate = currentSubscription.endDate.toDate();
    const now = new Date();
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));

    return {
      packageName: currentSubscription.packageName,
      daysLeft,
      isExpiring: daysLeft <= 7,
      usageStats: currentSubscription.usageStats || {},
      maxApplications: currentSubscription.maxApplications || 0,
    };
  };

  const isCurrentPackage = (pkg) => {
    if (!currentSubscription || !currentSubscription.purchaseHistory) return false;
    const lastPurchase = currentSubscription.purchaseHistory[currentSubscription.purchaseHistory.length - 1];
    return lastPurchase && lastPurchase.packageId === pkg.id;
  };

  if (loading) return <LoadingSpinner />;

  const subscriptionStatus = getSubscriptionStatus();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      
      {/* Header section */}
      <div className="text-center mb-12">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-[#0E3A61] tracking-tight mb-3">
          Career Progression Plans
        </h1>
        <p className="text-slate-600 max-w-2xl mx-auto text-sm sm:text-base font-medium px-2">
          Select an operational tier to activate application submission permissions and custom portfolio tools.
        </p>
      </div>

      {/* Current Subscription Status Panel (Restructured) */}
      {showCurrentPackage && currentSubscription && (
        <div className={`mb-10 p-6 rounded-3xl rounded-tr-[3.5rem] rounded-bl-[3.5rem] border ${subscriptionStatus?.isExpiring
          ? 'bg-amber-50/50 border-amber-200 shadow-sm'
          : 'bg-emerald-50/40 border-emerald-200/80 shadow-sm'
          }`}>
          <div className="p-2">
            
            {/* Header row with status and refresh button */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 border-b border-slate-200/50 pb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-white ${
                  subscriptionStatus?.isExpiring ? 'bg-amber-500' : 'bg-[#508D0C]'
                }`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h3 className={`text-lg font-black ${subscriptionStatus?.isExpiring ? 'text-amber-900' : 'text-[#0E3A61]'}`}>
                    {currentSubscription.packageName}
                  </h3>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                    Active Placement Level
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => fetchData()}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors text-xs font-bold text-slate-700 shadow-sm"
                  title="Refresh usage data"
                >
                  <svg className="w-4.5 h-4.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Sync Usage
                </button>
                {subscriptionStatus?.isExpiring && (
                  <span className="flex items-center gap-1.5 bg-amber-500 text-white px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider shadow-sm">
                    Expiring Soon
                  </span>
                )}
              </div>
            </div>

            {/* Benefit Grid columns inside panel */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Telemetry Column */}
              <div className="space-y-4">
                <h4 className="font-bold text-[#0E3A61] text-xs uppercase tracking-wider">System Telemetry</h4>
                
                {currentSubscription.maxApplications && (
                  <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
                    <div className="flex items-center justify-between mb-2.5">
                      <span className="text-xs font-bold text-slate-600 flex items-center gap-2">
                        <svg className="w-4 h-4 text-[#0E3A61]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Applications Dispatched
                      </span>
                      <span className={`text-xs font-black px-2.5 py-1 rounded-full ${
                        (currentSubscription.usageStats?.applicationsUsed || 0) >= (currentSubscription.maxApplications || 0) && currentSubscription.maxApplications !== 999999
                          ? 'bg-red-50 border border-red-200 text-red-600'
                          : 'bg-emerald-50 border border-emerald-100 text-[#508D0C]'
                      }`}>
                        {currentSubscription.usageStats?.applicationsUsed || 0} / {currentSubscription.maxApplications === 999999 ? '∞' : (currentSubscription.maxApplications || 0)}
                      </span>
                    </div>

                    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          (currentSubscription.usageStats?.applicationsUsed || 0) >= currentSubscription.maxApplications
                            ? 'bg-red-500'
                            : 'bg-[#0E3A61]'
                        }`}
                        style={{
                          width: `${currentSubscription.maxApplications === 999999
                            ? 0
                            : Math.min(((currentSubscription.usageStats?.applicationsUsed || 0) / (currentSubscription.maxApplications || 1)) * 100, 100)}%`
                        }}
                      ></div>
                    </div>

                    <p className="text-[10px] font-bold text-slate-400 mt-2.5 uppercase tracking-wider">
                      {currentSubscription.maxApplications === 999999
                        ? 'Uncapped application submissions authorized'
                        : `${(currentSubscription.maxApplications || 0) - (currentSubscription.usageStats?.applicationsUsed || 0)} application transmissions remaining`}
                    </p>
                  </div>
                )}
              </div>

              {/* Capabilities Column */}
              <div className="space-y-4">
                <h4 className="font-bold text-[#0E3A61] text-xs uppercase tracking-wider">Plan Capabilities</h4>
                
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 space-y-3.5">
                  {/* Validity */}
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-blue-50 border border-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Term Duration</p>
                      <p className={`text-xs font-black ${subscriptionStatus?.isExpiring ? 'text-amber-600' : 'text-[#508D0C]'}`}>
                        {subscriptionStatus?.daysLeft > 0
                          ? `${subscriptionStatus.daysLeft} Days Remaining`
                          : 'Expired'
                        }
                      </p>
                    </div>
                  </div>

                  {/* Resume Templates */}
                  {currentSubscription.resumeTemplates > 0 && (
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-purple-50 border border-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Curriculum Vitae Templates</p>
                        <p className="text-xs font-bold text-slate-800">{currentSubscription.resumeTemplates} Premium Templates Available</p>
                      </div>
                    </div>
                  )}

                  {/* Profile Boost */}
                  {currentSubscription.profileBoost && (
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-emerald-50 border border-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-[#508D0C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-[#508D0C] uppercase tracking-wider">Algorithmic Profile Promotion</p>
                        <p className="text-xs font-bold text-slate-800">System-wide visibility enhancement enabled</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Package Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {packages.map((pkg, index) => {
          const recommended = index === 1;
          const current = isCurrentPackage(pkg);
          
          return (
            <div
              key={pkg.id}
              className={`relative bg-white rounded-3xl rounded-tr-[3.5rem] rounded-bl-[3.5rem] shadow-md border-2 transition-all duration-300 hover:shadow-xl hover:scale-[1.03] flex flex-col justify-between ${
                recommended
                  ? 'border-[#0E3A61] shadow-[#0E3A61]/10 ring-4 ring-[#0E3A61]/5'
                  : 'border-slate-200/80 hover:border-slate-300'
              }`}
            >
              {/* Card Badges */}
              {recommended && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="bg-gradient-to-r from-[#0E3A61] to-[#E2822F] text-white px-5 py-1.5 rounded-full text-xs font-black tracking-wide shadow-md">
                    OPTIMAL CHOICE
                  </span>
                </div>
              )}

              {current && (
                <div className="absolute -top-4 right-6 z-10">
                  <span className="bg-[#508D0C] text-white px-3 py-1 rounded-full text-[10px] font-black tracking-wider shadow-sm">
                    ✓ ACTIVE ALLOCATION
                  </span>
                </div>
              )}

              {/* Card Body */}
              <div className="p-6 sm:p-8 flex-1 flex flex-col justify-between">
                
                <div>
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-black text-slate-800 mb-4">{pkg.name}</h3>

                    {/* Price structure */}
                    <div className="bg-gradient-to-br from-slate-50 to-blue-50/20 border border-slate-100 rounded-2xl p-4 mb-2">
                      <div className="flex justify-between items-center text-xs font-semibold text-slate-500 mb-1.5">
                        <span>Net Rate:</span>
                        <span className="font-bold text-slate-700">{formatAmount(pkg.price)}</span>
                      </div>
                      <div className="flex justify-between items-center text-xs font-semibold text-slate-500 mb-1.5">
                        <span>Regulatory Tax (GST 18%):</span>
                        <span className="font-bold text-slate-700">{formatAmount(calculateGST(pkg.price).gstAmount)}</span>
                      </div>
                      <div className="border-t border-slate-200/60 pt-2.5 mt-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-bold text-[#0E3A61] uppercase tracking-wider">Aggregate Cost:</span>
                          <span className="text-2xl font-black bg-gradient-to-r from-[#0E3A61] to-[#E2822F] bg-clip-text text-transparent">
                            {formatAmount(calculateGST(pkg.price).totalAmount)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <span className="text-slate-400 text-xs font-bold uppercase tracking-wider">
                      Access Period: {pkg.duration} Days
                    </span>
                  </div>

                  {pkg.description && (
                    <p className="text-xs text-slate-500 font-medium text-center mb-6 leading-relaxed">
                      {pkg.description}
                    </p>
                  )}

                  {/* Specific Capabilities */}
                  <div className="space-y-2 mb-6">
                    {pkg.maxApplications && (
                      <div className="flex items-center justify-center">
                        <div className="flex items-center bg-blue-50/60 border border-blue-100/50 px-4 py-1.5 rounded-full">
                          <svg className="w-3.5 h-3.5 text-[#0E3A61] mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-[#0E3A61] font-bold text-xs">
                            {pkg.maxApplications === 999999 ? 'Uncapped' : pkg.maxApplications} Job Applications
                          </span>
                        </div>
                      </div>
                    )}

                    {pkg.features && pkg.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center justify-center">
                        <div className="flex items-center bg-slate-50 border border-slate-100 px-4 py-1.5 rounded-full">
                          <svg className="w-3.5 h-3.5 text-[#508D0C] mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-slate-600 font-semibold text-xs">{feature}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Button Container */}
                <div>
                  <button
                    onClick={() => handlePurchase(pkg)}
                    disabled={purchasing === pkg.id || (pkg.price === 0 && hasUsedFree)}
                    className={`w-full py-3.5 px-6 rounded-xl font-bold text-sm uppercase tracking-wider transition-all duration-300 transform active:scale-95 shadow-md hover:shadow-lg ${
                      purchasing === pkg.id || (pkg.price === 0 && hasUsedFree)
                        ? 'bg-slate-300 text-slate-500 cursor-not-allowed shadow-none hover:shadow-none transform-none'
                        : recommended
                          ? 'bg-gradient-to-r from-[#0E3A61] to-[#E2822F] text-white hover:opacity-95'
                          : 'bg-slate-800 text-white hover:bg-slate-700'
                    }`}
                  >
                    {purchasing === pkg.id
                      ? 'Processing...'
                      : pkg.price === 0
                        ? hasUsedFree
                          ? 'Already Claimed'
                          : 'Claim Free Trial'
                        : 'Authorize Activation'
                    }
                  </button>

                  {currentSubscription && (
                    <p className="text-[10px] text-center text-slate-400 font-bold uppercase tracking-wider mt-2.5">
                      ✨ Purchased benefits will merge with the active operational plan.
                    </p>
                  )}
                </div>

              </div>
            </div>
          );
        })}
      </div>

      {packages.length === 0 && (
        <div className="text-center py-16 bg-white border border-slate-200/80 rounded-3xl p-8 max-w-md mx-auto">
          <div className="text-slate-300 text-5xl mb-3">📦</div>
          <h3 className="text-lg font-black text-[#0E3A61] mb-1">No Career Progression Plans Available</h3>
          <p className="text-slate-500 text-sm font-medium">
            No packages are currently active for job seekers. Please verify again later.
          </p>
        </div>
      )}
    </div>
  );
};

export default JobSeekerPackagePurchase;
